import { Injectable, inject } from '@angular/core';
import {
  PacketCraftClassLookup,
  PacketCraftLookup,
  PacketLookupItem,
  PacketRegistrationCompany,
} from '@ups/xplat/api/dto';
import {
  MyHttpClientFactory,
  ResponseCasingEnum,
  environment,
} from '@ups/xplat/core';
import { parseDataToLowerCamelCased } from '@ups/xplat/utils';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

/**
 * Viewpoint-backed HR-Edit lookups (states, crafts, payroll groups, etc.) from
 * the employee-packet-api `api/viewpointLookup/*` endpoints. Every call fails
 * soft to an empty list so a Viewpoint outage never blocks the screen — the
 * bound ComboBoxes are `allowCustom`, so saved values still display.
 * Pass `failSoft = false` where the caller must tell "failed" from "empty":
 * the Register dialog shows an error with Retry instead of an empty list.
 */
@Injectable({ providedIn: 'root' })
export class PacketLookupService {
  private readonly http = inject(MyHttpClientFactory).createHttpClient(
    environment.urls.packetAPI,
    true,
    ResponseCasingEnum.CamelCase
  );

  private list<T>(url: string, failSoft = true): Observable<T[]> {
    const items = this.http
      .get<T[]>(url)
      .pipe(map((r) => parseDataToLowerCamelCased<T[]>(r as never) ?? []));
    return failSoft ? items.pipe(catchError(() => of([] as T[]))) : items;
  }

  getStates(country?: string): Observable<PacketLookupItem[]> {
    const query = country ? `?country=${encodeURIComponent(country)}` : '';
    return this.list<PacketLookupItem>(`api/viewpointLookup/states${query}`);
  }

  getCountries(): Observable<PacketLookupItem[]> {
    return this.list<PacketLookupItem>('api/viewpointLookup/countries');
  }

  getEthnicities(prCo: number): Observable<PacketLookupItem[]> {
    return this.list<PacketLookupItem>(
      `api/viewpointLookup/ethnicities?prCo=${prCo}`
    );
  }

  getVeteranStatuses(): Observable<PacketLookupItem[]> {
    return this.list<PacketLookupItem>('api/viewpointLookup/veteranStatuses');
  }

  getEmployeeTypes(): Observable<PacketLookupItem[]> {
    return this.list<PacketLookupItem>('api/viewpointLookup/employeeTypes');
  }

  getPayrollGroups(prCo: number): Observable<PacketLookupItem[]> {
    return this.list<PacketLookupItem>(
      `api/viewpointLookup/payrollGroups?prCo=${prCo}`
    );
  }

  getPayrollDepartments(prCo: number): Observable<PacketLookupItem[]> {
    return this.list<PacketLookupItem>(
      `api/viewpointLookup/payrollDepartments?prCo=${prCo}`
    );
  }

  getCrafts(prCo: number): Observable<PacketCraftLookup[]> {
    return this.list<PacketCraftLookup>(
      `api/viewpointLookup/crafts?prCo=${prCo}`
    );
  }

  getClasses(
    prCo: number,
    craft: string
  ): Observable<PacketCraftClassLookup[]> {
    return this.list<PacketCraftClassLookup>(
      `api/viewpointLookup/classes?prCo=${prCo}&craft=${encodeURIComponent(craft)}`
    );
  }

  getInsuranceCodes(): Observable<PacketLookupItem[]> {
    return this.list<PacketLookupItem>('api/viewpointLookup/insuranceCodes');
  }

  getEarningCodes(
    prCo: number,
    prDept: string
  ): Observable<PacketLookupItem[]> {
    return this.list<PacketLookupItem>(
      `api/viewpointLookup/earningCodes?prCo=${prCo}&prDept=${encodeURIComponent(prDept)}`
    );
  }

  getBillableGroups(): Observable<PacketLookupItem[]> {
    return this.list<PacketLookupItem>('api/viewpointLookup/billableGroups');
  }

  getOccupationalCategories(prCo: number): Observable<PacketLookupItem[]> {
    return this.list<PacketLookupItem>(
      `api/viewpointLookup/occupationalCategories?prCo=${prCo}`
    );
  }

  getHrDepartments(failSoft = true): Observable<PacketLookupItem[]> {
    return this.list<PacketLookupItem>(
      'api/viewpointLookup/hrDepartments',
      failSoft
    );
  }

  getJobTitles(
    hrDepartment: string,
    failSoft = true
  ): Observable<PacketLookupItem[]> {
    return this.list<PacketLookupItem>(
      `api/viewpointLookup/jobTitles?hrDepartment=${encodeURIComponent(hrDepartment)}`,
      failSoft
    );
  }

  /** Supervisor lookup (Bug 35388): Viewpoint employees by name, value = HRRef. */
  searchEmployees(search: string): Observable<PacketLookupItem[]> {
    return this.list<PacketLookupItem>(
      `api/viewpointLookup/employees?search=${encodeURIComponent(search)}`
    );
  }

  getVacationLeaveCodes(prCo: number): Observable<PacketLookupItem[]> {
    return this.list<PacketLookupItem>(
      `api/viewpointLookup/vacationLeaveCodes?prCo=${prCo}`
    );
  }

  getSickLeaveCodes(prCo: number): Observable<PacketLookupItem[]> {
    return this.list<PacketLookupItem>(
      `api/viewpointLookup/sickLeaveCodes?prCo=${prCo}`
    );
  }

  getLicenseTypes(failSoft = true): Observable<PacketLookupItem[]> {
    return this.list<PacketLookupItem>(
      'api/viewpointLookup/licenseTypes',
      failSoft
    );
  }

  /** Register dialog hiring companies: the caller's Software Security companies enabled for Packet (WI 34883). Errors propagate. */
  getRegistrationCompanies(): Observable<PacketRegistrationCompany[]> {
    return this.list<PacketRegistrationCompany>(
      'api/employee/register/companies',
      false
    );
  }

  getCompanyLocations(): Observable<PacketLookupItem[]> {
    return this.list<PacketLookupItem>('api/viewpointLookup/companyLocations');
  }

  getNonBillJobs(prCo: number): Observable<PacketLookupItem[]> {
    return this.list<PacketLookupItem>(
      `api/viewpointLookup/nonBillJobs?prCo=${prCo}`
    );
  }

  getJobPhases(job: string): Observable<PacketLookupItem[]> {
    return this.list<PacketLookupItem>(
      `api/viewpointLookup/jobPhases?job=${encodeURIComponent(job)}`
    );
  }
}
